import React from 'react';
import {ForecastMapService} from '../api';
import {ForecastMapConfig} from '../api/types';
import useApiClient from './useApiClient';

export type ConfigLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

type ConfigResult = {
  key: string | null;
  status: 'ready' | 'error';
  config: ForecastMapConfig | null;
};

/**
 * Loads the config being edited (builder edit mode). `configId === null`
 * (new-map mode) stays idle.
 */
const useForecastMapConfig = (configId: number | null) => {
  const {fetch} = useApiClient();
  const [reloadKey, setReloadKey] = React.useState(0);
  const [result, setResult] = React.useState<ConfigResult>({
    key: null,
    status: 'ready',
    config: null,
  });

  const key = configId === null ? null : `${configId}:${reloadKey}`;

  React.useEffect(() => {
    if (configId === null || key === null) {
      return;
    }
    let cancelled = false;
    fetch(ForecastMapService.forecastMapConfigDetail(configId))
      .then((config) => {
        if (!cancelled) {
          setResult({key, status: 'ready', config});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResult({key, status: 'error', config: null});
        }
      });
    // Drop late responses when the config changes or on unmount.
    return () => {
      cancelled = true;
    };
  }, [configId, key, fetch]);

  const reload = React.useCallback(() => setReloadKey((k) => k + 1), []);

  const current = result.key === key && key !== null;
  const status: ConfigLoadStatus =
    key === null ? 'idle' : current ? result.status : 'loading';

  return {status, config: current ? result.config : null, reload};
};

export default useForecastMapConfig;
